// Web push (closed-browser study reminders): register the service worker,
// subscribe this device and keep the backend in sync.
import api from "./api";
import { getApiBaseUrl } from "./apiBase.js";

const FLAG = "eg-push-enabled";

export const pushSupported = () =>
  typeof window !== "undefined" &&
  window.isSecureContext &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

// iPhone/iPad only allow web push for sites added to the Home Screen
export const needsHomeScreenInstall = () => {
  const ios = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const standalone = window.matchMedia?.("(display-mode: standalone)").matches || navigator.standalone;
  return ios && !standalone;
};

const setFlag = (on) => {
  try {
    if (on) localStorage.setItem(FLAG, "1");
    else localStorage.removeItem(FLAG);
  } catch {
    /* storage unavailable */
  }
};

export const pushEnabledOnThisDevice = () => {
  try {
    return localStorage.getItem(FLAG) === "1";
  } catch {
    return false;
  }
};

const urlBase64ToUint8Array = (base64) => {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
};

const registerWorker = () =>
  navigator.serviceWorker.register(`/sw.js?api=${encodeURIComponent(getApiBaseUrl())}`, { scope: "/" });

export const currentSubscription = async () => {
  if (!pushSupported()) return null;
  const reg = await navigator.serviceWorker.getRegistration("/");
  return reg ? reg.pushManager.getSubscription() : null;
};

export const enablePush = async () => {
  if (!pushSupported()) throw new Error("This browser doesn't support closed-browser reminders.");
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error("Notifications are blocked. Allow them for this site, then try again.");
  }
  const reg = await registerWorker();
  await navigator.serviceWorker.ready;
  const { data } = await api.get("/api/push/vapid-public-key");
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.public_key),
    });
  }
  const json = sub.toJSON();
  await api.post("/api/push/subscribe", {
    endpoint: json.endpoint,
    keys: json.keys,
    device: navigator.userAgent.slice(0, 120),
  });
  setFlag(true);
  return true;
};

export const disablePush = async () => {
  const sub = await currentSubscription();
  if (sub) {
    try {
      await api.post("/api/push/unsubscribe", { endpoint: sub.endpoint });
    } finally {
      await sub.unsubscribe();
    }
  }
  setFlag(false);
};

export const sendTestPush = () => api.post("/api/push/test");

// Keep the flag honest if the browser dropped the subscription
export const syncPushState = async () => {
  if (!pushSupported()) return false;
  const sub = await currentSubscription();
  setFlag(Boolean(sub));
  return Boolean(sub);
};
